const FALLBACK_SITE_URL = "https://example.com";
const DEFAULT_TITLE = "Pasla | Futbol Oyuncusu, Takım ve Maç Bul";
const DEFAULT_DESCRIPTION =
  "Pasla çok yakında yayında. Futbol oyuncusu bul, takımını tamamla, rakip keşfet ve maçlarını daha hızlı organize et.";
const DEFAULT_IMAGE_PATH = "/logo.png";

const seoConfig = {
  siteName: "Pasla",
  locale: "tr_TR",
  publishedLocale: "tr-TR",
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  keywords: [
    "halı saha",
    "futbol oyuncusu bul",
    "takım bul",
    "rakip bul",
    "maç organize et",
    "Pasla",
  ],
};

function normalizeUrl(url) {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}

export function resolveSiteUrl() {
  const envSiteUrl = process.env.REACT_APP_SITE_URL?.trim();

  if (envSiteUrl) {
    return normalizeUrl(envSiteUrl);
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return normalizeUrl(window.location.origin);
  }

  return FALLBACK_SITE_URL;
}

function ensureMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function ensureLink(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function ensureJsonLd(id, data) {
  let element = document.head.querySelector(`#${id}`);

  if (!element) {
    element = document.createElement("script");
    element.type = "application/ld+json";
    element.id = id;
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

export function applySeo({
  title = seoConfig.title,
  description = seoConfig.description,
  path = "/",
  imagePath = DEFAULT_IMAGE_PATH,
} = {}) {
  const siteUrl = resolveSiteUrl();
  const canonicalUrl = path === "/" ? `${siteUrl}/` : `${siteUrl}${path}`;
  const imageUrl = imagePath.startsWith("http")
    ? imagePath
    : `${siteUrl}${imagePath}`;

  document.documentElement.lang = "tr";
  document.title = title;

  ensureMeta('meta[name="description"]', {
    name: "description",
    content: description,
  });
  ensureMeta('meta[name="keywords"]', {
    name: "keywords",
    content: seoConfig.keywords.join(", "),
  });
  ensureMeta('meta[name="robots"]', {
    name: "robots",
    content:
      "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  });
  ensureMeta('meta[property="og:locale"]', {
    property: "og:locale",
    content: seoConfig.locale,
  });
  ensureMeta('meta[property="og:type"]', {
    property: "og:type",
    content: "website",
  });
  ensureMeta('meta[property="og:site_name"]', {
    property: "og:site_name",
    content: seoConfig.siteName,
  });
  ensureMeta('meta[property="og:title"]', {
    property: "og:title",
    content: title,
  });
  ensureMeta('meta[property="og:description"]', {
    property: "og:description",
    content: description,
  });
  ensureMeta('meta[property="og:url"]', {
    property: "og:url",
    content: canonicalUrl,
  });
  ensureMeta('meta[property="og:image"]', {
    property: "og:image",
    content: imageUrl,
  });
  ensureMeta('meta[property="og:image:alt"]', {
    property: "og:image:alt",
    content: "Pasla logosu",
  });
  ensureMeta('meta[name="twitter:card"]', {
    name: "twitter:card",
    content: "summary_large_image",
  });
  ensureMeta('meta[name="twitter:title"]', {
    name: "twitter:title",
    content: title,
  });
  ensureMeta('meta[name="twitter:description"]', {
    name: "twitter:description",
    content: description,
  });
  ensureMeta('meta[name="twitter:image"]', {
    name: "twitter:image",
    content: imageUrl,
  });

  ensureLink('link[rel="canonical"]', {
    rel: "canonical",
    href: canonicalUrl,
  });
  ensureLink('link[rel="alternate"][hreflang="tr-TR"]', {
    rel: "alternate",
    hreflang: "tr-TR",
    href: canonicalUrl,
  });
  ensureLink('link[rel="alternate"][hreflang="x-default"]', {
    rel: "alternate",
    hreflang: "x-default",
    href: canonicalUrl,
  });

  ensureJsonLd("pasla-website-schema", {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: seoConfig.siteName,
    url: `${siteUrl}/`,
    inLanguage: seoConfig.publishedLocale,
    description,
  });

  ensureJsonLd("pasla-organization-schema", {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: seoConfig.siteName,
    url: `${siteUrl}/`,
    logo: imageUrl,
    description,
  });

  ensureJsonLd("pasla-webpage-schema", {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    url: canonicalUrl,
    description,
    inLanguage: seoConfig.publishedLocale,
    isPartOf: {
      "@type": "WebSite",
      name: seoConfig.siteName,
      url: `${siteUrl}/`,
    },
  });
}

export { seoConfig };
